import React, { useState, useEffect, useMemo, useRef } from 'react';
import { jobsAPI, billingItemsAPI } from '../../lib/api';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../ui/command';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { useBeforeUnload, ConfirmDiscardDialog, guardedContentProps, useNavigationGuard } from '../common/UnsavedGuard';
import { Plus, Trash2, Pencil, Check, ChevronsUpDown, CalendarClock } from 'lucide-react';

const HOLIDAY_METHODS = ['None', 'STD', 'PFL'];

const emptyLine = {
  billing_item_id: '',
  line_flavor: 'labor',
  bill_rate: 0,
  rate: 0,
  quantity: 0,
  uom: '',
  hrs_desc: '',
  holiday: false,
  holiday_method: 'None',
  tax: false,
  def_flag: false,
  gl_number: '',
  supervisor_employee_id: '',
  winteam_bill_code_id: '',
};

/** Category-grouped combobox for Billing Items. Hides inactive items and
 *  floats a "Most used" section at the top. `usage` is a map of
 *  billing_item_id → job-wide-usage-count from the parent. */
function BillingItemPicker({ value, onChange, items, usage }) {
  const [open, setOpen] = useState(false);
  const active = useMemo(() => items.filter(i => i.is_active), [items]);
  const selected = useMemo(() => items.find(i => i.id === value), [items, value]);
  const mostUsed = useMemo(() => {
    return [...active]
      .filter(i => (usage[i.id] || 0) > 0)
      .sort((a, b) => (usage[b.id] || 0) - (usage[a.id] || 0))
      .slice(0, 8);
  }, [active, usage]);
  const grouped = useMemo(() => {
    const map = new Map();
    active.forEach(i => {
      if (!map.has(i.category)) map.set(i.category, []);
      map.get(i.category).push(i);
    });
    return [...map.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [active]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          className="w-full justify-between text-left font-normal h-9"
          data-testid="rate-line-billing-item-trigger"
        >
          <span className="truncate">{selected?.name || 'Select a billing item…'}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[min(420px,calc(100vw-2rem))] p-0" align="start">
        <Command>
          <CommandInput placeholder="Search billing items…" />
          <CommandList className="max-h-[360px]">
            <CommandEmpty>No items found.</CommandEmpty>
            {mostUsed.length > 0 && (
              <CommandGroup heading="Most used">
                {mostUsed.map(i => (
                  <CommandItem
                    key={`mu-${i.id}`}
                    value={`mu ${i.name}`}
                    onSelect={() => { onChange(i.id); setOpen(false); }}
                    data-testid={`rate-line-item-${i.id}-most-used`}
                  >
                    <Check className={`mr-2 h-4 w-4 ${value === i.id ? 'opacity-100' : 'opacity-0'}`} />
                    <div className="flex-1">
                      <p className="text-sm">{i.name}</p>
                      <p className="text-xs text-slate-400">{i.category}</p>
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
            {grouped.map(([cat, list]) => (
              <CommandGroup key={cat} heading={cat}>
                {list.map(i => (
                  <CommandItem
                    key={i.id}
                    value={`${cat} ${i.name}`}
                    onSelect={() => { onChange(i.id); setOpen(false); }}
                    data-testid={`rate-line-item-${i.id}`}
                  >
                    <Check className={`mr-2 h-4 w-4 ${value === i.id ? 'opacity-100' : 'opacity-0'}`} />
                    {i.name}
                  </CommandItem>
                ))}
              </CommandGroup>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

/** Full rate-lines editor. Handles create/edit/delete inline. */
export default function RateLinesEditor({ jobId, canEdit, meta }) {
  const [lines, setLines] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState({ ...emptyLine });
  const [saving, setSaving] = useState(false);
  const draftBaseline = useRef('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const draftDirty = useMemo(
    () => !!editingId && JSON.stringify(draft) !== draftBaseline.current,
    [draft, editingId],
  );
  useBeforeUnload(draftDirty);
  // Effective dating (Phase 1A): once a rate has been superseded, the row is
  // history and only a dated change is allowed.
  const [hasHistory, setHasHistory] = useState(false);
  const [changing, setChanging] = useState(null);

  const applyChange = async () => {
    if (!changing) return;
    setSaving(true);
    try {
      await jobsAPI.changeRateLine(changing.line.id, {
        bill_rate: parseFloat(changing.bill_rate) || 0,
        effective_start_date: changing.effective_start_date || null,
        reason: changing.reason || '',
      });
      toast.success('New rate line created; the previous rate was end-dated');
      setChanging(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not change the rate');
    } finally { setSaving(false); }
  };

  const load = async () => {
    setLoading(true);
    try {
      // The billing-item catalog is a separate capability (billing_items:view).
      // A viewer who lacks it must still see the rate lines, so the two reads
      // are independent — a 403 on the catalog only costs the item names.
      const [linesRes, itemsRes] = await Promise.allSettled([
        jobsAPI.rateHistory(jobId),
        billingItemsAPI.list(),
      ]);
      if (linesRes.status === 'rejected') throw linesRes.reason;
      setLines(linesRes.value.data.lines);
      setHasHistory(!!linesRes.value.data.has_billed_history);
      setItems(itemsRes.status === 'fulfilled' ? itemsRes.value.data : []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load rate lines');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (jobId) load(); }, [jobId]);

  const usageMap = useMemo(() => {
    const m = {};
    lines.forEach(l => { m[l.billing_item_id] = (m[l.billing_item_id] || 0) + 1; });
    return m;
  }, [lines]);

  const itemName = (id) => items.find(i => i.id === id)?.name || '—';

  const beginAdd = () => {
    const d = { ...emptyLine };
    setEditingId('__new__');
    setDraft(d);
    draftBaseline.current = JSON.stringify(d);
  };
  const beginEdit = (line) => {
    const d = {
      billing_item_id: line.billing_item_id || '',
      line_flavor: line.line_flavor || 'labor',
      bill_rate: line.bill_rate || 0,
      rate: line.rate || 0,
      quantity: line.quantity || 0,
      uom: line.uom || '',
      hrs_desc: line.hrs_desc || '',
      holiday: !!line.holiday,
      holiday_method: line.holiday_method || 'None',
      tax: !!line.tax,
      def_flag: !!line.def_flag,
      gl_number: line.gl_number || '',
      supervisor_employee_id: line.supervisor_employee_id || '',
      winteam_bill_code_id: line.winteam_bill_code_id || '',
    };
    setEditingId(line.id);
    setDraft(d);
    draftBaseline.current = JSON.stringify(d);
  };
  const cancel = () => { setEditingId(null); setDraft({ ...emptyLine }); setConfirmOpen(false); };
  // C1: never discard rate-line edits without asking.
  const requestCancel = () => { if (draftDirty) setConfirmOpen(true); else cancel(); };

  const save = async () => {
    if (!draft.billing_item_id) { toast.error('Select a billing item'); return false; }
    setSaving(true);
    try {
      const payload = {
        ...draft,
        bill_rate: parseFloat(draft.bill_rate) || 0,
        rate: parseFloat(draft.rate) || 0,
        quantity: parseFloat(draft.quantity) || 0,
      };
      if (editingId === '__new__') {
        await jobsAPI.addRateLine(jobId, payload);
        toast.success('Rate line added');
      } else {
        await jobsAPI.updateRateLine(editingId, payload);
        toast.success('Rate line updated');
      }
      cancel();
      await load();
      return true;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
      return false;
    } finally {
      setSaving(false);
    }
  };

  // App-wide guard: menu clicks, in-app links and browser Back/Forward also prompt.
  useNavigationGuard('rate-line', {
    getDirty: () => draftDirty,
    save,
    discard: cancel,
  });

  const remove = async (lineId) => {
    if (!window.confirm('Delete this rate line?')) return;
    try {
      await jobsAPI.deleteRateLine(lineId);
      toast.success('Rate line deleted');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    }
  };

  const uom = (meta?.lookups?.uom || []).map(x => x.value);
  const hrsDesc = (meta?.lookups?.hrs_desc || []).map(x => x.value);

  const extension = useMemo(() => {
    if (draft.line_flavor !== 'equipment') return 0;
    return (parseFloat(draft.rate) || 0) * (parseFloat(draft.quantity) || 0);
  }, [draft.line_flavor, draft.rate, draft.quantity]);

  return (
    <div className="space-y-3" data-testid="rate-lines-editor">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-semibold text-slate-700">Rate lines ({lines.length})</h4>
        {canEdit && !editingId && (
          <Button size="sm" onClick={beginAdd} data-testid="add-rate-line-btn">
            <Plus className="w-4 h-4 mr-1.5" /> Add rate line
          </Button>
        )}
      </div>

      {changing && (
        <div className="border border-line rounded-lg p-4 bg-amber-50/50 space-y-3"
          data-testid="rate-change-panel">
          <p className="text-sm font-medium">
            New rate for {itemName(changing.line.billing_item_id)}
            {changing.line.hrs_desc ? ` · ${changing.line.hrs_desc}` : ''}
          </p>
          <p className="text-xs text-slate-600 max-w-xl">
            The current rate of ${Number(changing.line.bill_rate || 0).toFixed(4)} is end-dated
            the day before the new one starts, so an invoice from last month can still be
            reproduced. Rates are stored to four decimal places and never rounded here.
          </p>
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <Label className="text-xs">New bill rate</Label>
              <Input className="h-9 w-32 font-mono" type="number" step="0.0001"
                value={changing.bill_rate}
                onChange={(e) => setChanging(c => ({ ...c, bill_rate: e.target.value }))}
                data-testid="rate-change-rate" />
            </div>
            <div>
              <Label className="text-xs">Effective from</Label>
              <Input className="h-9 w-40" type="date" value={changing.effective_start_date}
                onChange={(e) => setChanging(c => ({ ...c, effective_start_date: e.target.value }))}
                data-testid="rate-change-date" />
            </div>
            <div className="flex-1 min-w-[220px]">
              <Label className="text-xs">Reason</Label>
              <Input className="h-9" value={changing.reason}
                placeholder="Annual increase, contract amendment…"
                onChange={(e) => setChanging(c => ({ ...c, reason: e.target.value }))}
                data-testid="rate-change-reason" />
            </div>
            <Button variant="ghost" onClick={() => setChanging(null)}>Cancel</Button>
            <Button onClick={applyChange} disabled={saving} data-testid="rate-change-apply">
              {saving ? 'Saving…' : 'Create the new rate line'}
            </Button>
          </div>
        </div>
      )}

      {loading ? (
        <p className="text-sm text-slate-400">Loading…</p>
      ) : (
        <div className="rounded-md border border-slate-200 overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="min-w-[220px]">Billing Item</TableHead>
                <TableHead>Flavor</TableHead>
                <TableHead className="text-right">Bill Rate</TableHead>
                <TableHead className="text-right">Rate</TableHead>
                <TableHead className="text-right">Qty</TableHead>
                <TableHead className="text-right">Extension</TableHead>
                <TableHead>UoM</TableHead>
                <TableHead>Hrs Desc</TableHead>
                <TableHead>Flags</TableHead>
                {canEdit && <TableHead className="w-28 text-right">Actions</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {lines.length === 0 && (
                <TableRow>
                  <TableCell colSpan={canEdit ? 10 : 9} className="text-center py-8 text-sm text-slate-400">
                    No rate lines yet. {canEdit ? 'Click "Add rate line" to create the first one.' : ''}
                  </TableCell>
                </TableRow>
              )}
              {lines.map(line => (
                  <TableRow key={line.id} data-testid={`rate-line-row-${line.id}`}>
                    <TableCell className="font-medium">{itemName(line.billing_item_id)}</TableCell>
                    <TableCell>
                      <Badge variant={line.line_flavor === 'equipment' ? 'secondary' : 'outline'} className="text-xs">
                        {line.line_flavor}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right font-mono text-sm">
                      ${Number(line.bill_rate || 0).toFixed(4).replace(/0+$/, '').replace(/\.$/, '.00')}
                      {line.needs_rate && (
                        <Badge variant="outline" className="ml-1 text-[10px] text-amber-600 border-amber-300">
                          needs a rate
                        </Badge>
                      )}
                      <div className="text-[10px] text-slate-500 font-sans">
                        {line.effective_start_date || '—'} → {line.effective_end_date || 'open'}
                        {line.source ? ` · ${line.source.replace('_', ' ')}` : ''}
                      </div>
                    </TableCell>
                    <TableCell className="text-right font-mono text-sm">${Number(line.rate || 0).toFixed(2)}</TableCell>
                    <TableCell className="text-right font-mono text-sm">{Number(line.quantity || 0).toFixed(2)}</TableCell>
                    <TableCell className="text-right font-mono text-sm">${Number(line.extension || 0).toFixed(2)}</TableCell>
                    <TableCell className="text-xs">{line.uom || '—'}</TableCell>
                    <TableCell className="text-xs">{line.hrs_desc || '—'}</TableCell>
                    <TableCell>
                      <div className="flex gap-1 flex-wrap">
                        {line.holiday && <Badge variant="outline" className="text-[10px]">Hol</Badge>}
                        {line.tax && <Badge variant="outline" className="text-[10px]">Tax</Badge>}
                        {line.def_flag && <Badge variant="outline" className="text-[10px]">DEF</Badge>}
                        {line.holiday_method && line.holiday_method !== 'None' && (
                          <Badge variant="outline" className="text-[10px]">{line.holiday_method}</Badge>
                        )}
                      </div>
                    </TableCell>
                    {canEdit && (
                      <TableCell className="text-right">
                        {/* A rate change on a job with history is a NEW
                            effective-dated row — there is deliberately no
                            in-place edit path for the rate. */}
                        <Button size="icon" variant="ghost" title="Change the rate from a date"
                          onClick={() => setChanging({ line, bill_rate: line.bill_rate || 0, effective_start_date: '', reason: '' })}
                          data-testid={`change-rate-line-${line.id}`}>
                          <CalendarClock className="w-4 h-4" />
                        </Button>
                        {!hasHistory && !line.effective_end_date && (
                          <Button size="icon" variant="ghost" onClick={() => beginEdit(line)} data-testid={`edit-rate-line-${line.id}`}>
                            <Pencil className="w-4 h-4" />
                          </Button>
                        )}
                        {!hasHistory && (
                          <Button size="icon" variant="ghost" onClick={() => remove(line.id)} data-testid={`delete-rate-line-${line.id}`}>
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        )}
                      </TableCell>
                    )}
                  </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {canEdit && (
        <RateLineDialog
          open={!!editingId}
          isNew={editingId === '__new__'}
          draft={draft} setDraft={setDraft} items={items} usage={usageMap}
          uom={uom} hrsDesc={hrsDesc} extension={extension}
          onSave={save} onRequestClose={requestCancel} saving={saving}
        />
      )}
      <ConfirmDiscardDialog
        open={confirmOpen}
        saving={saving}
        onSave={async () => { await save(); }}
        onDiscard={cancel}
        onCancel={() => setConfirmOpen(false)}
      />
    </div>
  );
}

function RateLineDialog({ open, isNew, draft, setDraft, items, usage, uom, hrsDesc, extension, onSave, onRequestClose, saving }) {
  const change = (k, v) => setDraft(d => ({ ...d, [k]: v }));
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onRequestClose(); }}>
      {/* C3: a roomy form whose fields stack on small screens and sit three-up
          on a laptop (≥1366px) with no horizontal scroll. */}
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="rate-line-dialog"
        {...guardedContentProps(onRequestClose)}>
        <DialogHeader>
          <DialogTitle>{isNew ? 'Add rate line' : 'Edit rate line'}</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <div className="sm:col-span-2 lg:col-span-3">
            <Label className="text-xs">Billing item</Label>
            <BillingItemPicker
              value={draft.billing_item_id}
              onChange={(id) => change('billing_item_id', id)}
              items={items}
              usage={usage}
            />
          </div>
          <div>
            <Label className="text-xs">Flavor</Label>
            <Select value={draft.line_flavor} onValueChange={(v) => change('line_flavor', v)}>
              <SelectTrigger className="w-full" data-testid="rate-line-flavor-trigger"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="labor">Labor</SelectItem>
                <SelectItem value="equipment">Equipment</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Bill rate</Label>
            <Input type="number" step="0.01" value={draft.bill_rate}
              onChange={(e) => change('bill_rate', e.target.value)}
              className="w-full text-right font-mono" data-testid="rate-line-bill-rate" />
          </div>
          <div>
            <Label className="text-xs">Rate</Label>
            <Input type="number" step="0.01" value={draft.rate}
              onChange={(e) => change('rate', e.target.value)}
              disabled={draft.line_flavor === 'labor'}
              className="w-full text-right font-mono" data-testid="rate-line-rate" />
          </div>
          <div>
            <Label className="text-xs">Quantity</Label>
            <Input type="number" step="0.01" value={draft.quantity}
              onChange={(e) => change('quantity', e.target.value)}
              disabled={draft.line_flavor === 'labor'}
              className="w-full text-right font-mono" data-testid="rate-line-quantity" />
          </div>
          <div>
            <Label className="text-xs">Extension</Label>
            <div className="h-9 flex items-center px-3 rounded-md bg-slate-50 border border-slate-200 font-mono text-sm text-slate-600"
              data-testid="rate-line-extension">
              ${extension.toFixed(2)}
            </div>
          </div>
          <div>
            <Label className="text-xs">UoM</Label>
            <Select value={draft.uom || '__none__'} onValueChange={(v) => change('uom', v === '__none__' ? '' : v)}>
              <SelectTrigger className="w-full" data-testid="rate-line-uom"><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">—</SelectItem>
                {uom.map(v => <SelectItem key={v} value={v}>{v}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Hrs desc</Label>
            <Select value={draft.hrs_desc || '__none__'} onValueChange={(v) => change('hrs_desc', v === '__none__' ? '' : v)}>
              <SelectTrigger className="w-full" data-testid="rate-line-hrs-desc"><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">—</SelectItem>
                {hrsDesc.map(v => <SelectItem key={v} value={v}>{v}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Holiday method</Label>
            <Select value={draft.holiday_method} onValueChange={(v) => change('holiday_method', v)}>
              <SelectTrigger className="w-full" data-testid="rate-line-holiday-method"><SelectValue /></SelectTrigger>
              <SelectContent>
                {HOLIDAY_METHODS.map(v => <SelectItem key={v} value={v}>{v}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="sm:col-span-2 lg:col-span-3 flex flex-wrap gap-6 pt-1">
            <div className="flex items-center gap-2">
              <Switch checked={draft.holiday} onCheckedChange={(v) => change('holiday', v)} data-testid="rate-line-holiday" />
              <Label className="text-xs">Holiday</Label>
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={draft.tax} onCheckedChange={(v) => change('tax', v)} data-testid="rate-line-tax" />
              <Label className="text-xs">Tax</Label>
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={draft.def_flag} onCheckedChange={(v) => change('def_flag', v)} data-testid="rate-line-def" />
              <Label className="text-xs">DEF</Label>
            </div>
          </div>
        </div>
        <div className="sticky bottom-0 z-10 -mx-6 -mb-6 mt-2 flex justify-end gap-2 border-t bg-background px-6 py-4">
          <Button variant="outline" onClick={onRequestClose} disabled={saving} data-testid="rate-line-cancel">Cancel</Button>
          <Button onClick={onSave} disabled={saving} data-testid="rate-line-save">
            {saving ? 'Saving…' : (isNew ? 'Add rate line' : 'Save')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
